import type { AptosSignInOutput } from '@aptos-labs/siwa';
import { serializeSignInOutput } from '@aptos-labs/siwa';
import { saveToken, removeToken } from './token';
import * as Sentry from '@sentry/react';

const BACKEND_URL =
	process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
const AUTH_DEBUG = process.env.REACT_APP_AUTH_DEBUG === 'true';

const authDebugLog = (stage: string, details?: Record<string, unknown>) => {
	if (!AUTH_DEBUG) return;
	console.info(`[auth-debug] ${stage}`, details ?? {});
};

/**
 * Fetch the SIWA input (nonce, domain, statement) from the backend
 */
export async function fetchSignInInput() {
	return Sentry.startSpan(
		{
			op: 'http.client',
			name: 'GET /auth/siwa',
		},
		async (span) => {
			try {
				authDebugLog('fetchSignInInput:start', {
					backendUrl: BACKEND_URL,
				});
				const response = await fetch(`${BACKEND_URL}/auth/siwa`, {
					method: 'GET',
				});

				span.setAttribute('http.status_code', response.status);

				if (!response.ok) {
					const error = new Error('Failed to fetch sign-in input');
					Sentry.captureException(error, {
						tags: { operation: 'fetchSignInInput' },
						contexts: {
							http: {
								status: response.status,
								statusText: response.statusText,
							},
						},
					});
					throw error;
				}

				const data = await response.json();
				authDebugLog('fetchSignInInput:success', {
					status: response.status,
					domain: data?.data?.domain,
					uri: data?.data?.uri,
					noncePrefix:
						typeof data?.data?.nonce === 'string'
							? data.data.nonce.slice(0, 8)
							: undefined,
				});
				return data;
			} catch (error) {
				authDebugLog('fetchSignInInput:error', {
					error: error instanceof Error ? error.message : String(error),
				});
				Sentry.captureException(error, {
					tags: { operation: 'fetchSignInInput' },
				});
				throw error;
			}
		},
	);
}

/**
 * Send the signed SIWA output to backend for verification and token creation
 */
export async function loginWithSignInOutput(
	output: AptosSignInOutput,
	input: { nonce: string; domain: string; uri: string; statement: string },
) {
	return Sentry.startSpan(
		{
			op: 'http.client',
			name: 'POST /auth/siwa/callback',
		},
		async (span) => {
			try {
				// Serialize the output to the format expected by the backend
				const serializedOutput = serializeSignInOutput(output);
				authDebugLog('loginWithSignInOutput:start', {
					backendUrl: BACKEND_URL,
					inputDomain: input.domain,
					inputUri: input.uri,
					noncePrefix: input.nonce.slice(0, 8),
					address: serializedOutput?.input?.address,
				});
				Sentry.logger.debug('Serialized SIWA output', {
					nonce: input.nonce,
				});

				const response = await fetch(
					`${BACKEND_URL}/auth/siwa/callback`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							output: serializedOutput,
							input,
						}),
					},
				);

				span.setAttribute('http.status_code', response.status);

				if (!response.ok) {
					let errorMessage = 'Failed to login';
					try {
						const error = await response.json();
						errorMessage = error.error || errorMessage;
						authDebugLog('loginWithSignInOutput:server-error', {
							status: response.status,
							statusText: response.statusText,
							errorMessage,
						});
					} catch (e) {
						// Response wasn't JSON
						errorMessage = `Server error: ${response.status} ${response.statusText}`;
						authDebugLog('loginWithSignInOutput:server-error-nonjson', {
							status: response.status,
							statusText: response.statusText,
							errorMessage,
						});
					}

					const loginError = new Error(errorMessage);
					Sentry.captureException(loginError, {
						tags: { operation: 'loginWithSignInOutput' },
						contexts: {
							auth: {
								status: response.status,
								statusText: response.statusText,
								errorMessage,
							},
						},
					});
					throw loginError;
				}

				const data = await response.json();

				// Save the JWT token from the response
				if (data.data?.token) {
					saveToken(data.data.token);
					span.setAttribute('tokenSaved', true);
				}
				authDebugLog('loginWithSignInOutput:success', {
					status: response.status,
					address: data?.data?.address,
					tokenSaved: Boolean(data?.data?.token),
				});

				return data;
			} catch (error) {
				authDebugLog('loginWithSignInOutput:error', {
					error: error instanceof Error ? error.message : String(error),
				});
				Sentry.captureException(error, {
					tags: { operation: 'loginWithSignInOutput' },
				});
				throw error;
			}
		},
	);
}

/**
 * Logout by clearing the token and notifying backend
 */
export async function logout() {
	return Sentry.startSpan(
		{
			op: 'http.client',
			name: 'POST /auth/logout',
		},
		async (span) => {
			try {
				const response = await fetch(`${BACKEND_URL}/auth/logout`, {
					method: 'POST',
				});

				span.setAttribute('http.status_code', response.status);

				if (!response.ok) {
					const error = new Error('Failed to logout');
					Sentry.captureException(error, {
						tags: { operation: 'logout' },
						contexts: {
							http: {
								status: response.status,
								statusText: response.statusText,
							},
						},
					});
					throw error;
				}

				return response.json();
			} catch (error) {
				Sentry.captureException(error, {
					tags: { operation: 'logout' },
				});
				throw error;
			}
		},
	);
}
